import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import CancelIcon from '@mui/icons-material/Cancel';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import {
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    LinearProgress,
    Skeleton,
    Stack,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import useAxios from '../../../../hooks/useAxios';
import useDashboardAccount from '../../../../hooks/useDashboardAccount';
import { MetricCrossSubscriberEarningsResult } from '../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import useSextforceMetricsGroups, { OnlyFansMetricsGroupWithSummary } from '../../../../hooks/useSextforceMetricsGroups';
import useSubscribers from '../../../../hooks/useSubscribers';
import { SettingsContext } from '../../../../store/SettingsContext';
import { d2f, f2d, metricTypeName } from '../../../../utils/common';
import SextforceMetricsCrossReferenceEarningsSubscriberSelector from './SextforceMetricsCrossReferenceEarningsSubscriberSelector';
import SextforceMetricsGroupDialog from './SextforceMetricsGroupDialog';
import SextforceMetricsGroupsShareDialog from './SextforceMetricsGroupsShareDialog';
import SextforceMetricsGroupsTable from './SextforceMetricsGroupsTable';

// Map to track cross-pollination earnings by metric ID
type CrossPollinationEarningsMap = Map<string, MetricCrossSubscriberEarningsResult | null>;

// Map to track loading state by metric ID
type CrossPollinationLoadingMap = Map<string, boolean>;

// One row of the deduped cross-earnings response: earnings of one metric's attributed fans
// on one cross account
interface DedupedCrossEarningsRow extends Omit<MetricCrossSubscriberEarningsResult, '_id'> {
    metricId: string;
    metricSubscriberId: string;
    crossSubscriberId: string;
    fansCount: number;
}

interface DedupedCrossEarningsResponse {
    computedAt: string;
    fansConsidered: number;
    attributedFansByMetric: { metricId: string; fansCount: number }[];
    rows: DedupedCrossEarningsRow[];
    totals: Omit<MetricCrossSubscriberEarningsResult, '_id'>;
}

/**
 * Transforms the transactions data rows from the server to a format ready for export to CSV or Excel
 */
const transformTransactionsForExport = (
    rows: OnlyFansMetricsGroupWithSummary[],
    amountType: 'gross' | 'net',
    timezone: string,
    crossPollinationEarningsMap: CrossPollinationEarningsMap,
) => {
    const headers = [
        {
            label: 'Username',
            value: 'username',
        },
        {
            label: 'ID',
            value: 'foreignId',
        },
        {
            label: 'Type',
            value: 'type',
        },
        {
            label: 'Active',
            value: 'active',
        },
        {
            label: `Created At (${timezone})`,
            value: 'createdAt',
        },
        {
            label: 'OnlyFans Name',
            value: 'name',
        },
        {
            label: 'Platform',
            value: 'platform',
        },
        {
            label: '@ Username',
            value: 'associatedUsername',
        },
        {
            label: 'Subscribers Total',
            value: 'subscribersCount',
        },
        {
            label: 'Claims Total',
            value: 'claimsCount',
        },
        {
            label: 'Clicks Total',
            value: 'clicksCount',
        },
        {
            label: `Earnings ${amountType === 'gross' ? 'Gross' : 'Net'} Total (USD)`,
            value: 'earningsTotal',
        },
        {
            label: `Cross-Pollination Earnings ${amountType === 'gross' ? 'Gross' : 'Net'} Total (USD)`,
            value: 'crossPollinationEarnings',
        },
    ];

    const data = rows.map((group: OnlyFansMetricsGroupWithSummary) => {
        const metrics: {
            username: string;
            foreignId: number | string | undefined;
            type: string;
            active: string | boolean;
            createdAt: string;
            name: string;
            platform: string;
            associatedUsername: string;
            subscribersCount: number | string | undefined;
            claimsCount: number | string | undefined;
            clicksCount: number | string | undefined;
            earningsTotal: number | string;
            crossPollinationEarnings: number | string;
        }[] = group.metrics.map(metric => {
            const earningsTotal =
                metric.payload && metric.payload.earningsTotal && typeof metric.payload.earningsTotal === 'object'
                    ? d2f(metric.payload.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                    : 0;
            const claimsCount = (metric.payload && metric.payload.counters && metric.payload.counters.claimsCount) || undefined;
            const subscribersCount = (metric.payload && metric.payload.counters && metric.payload.counters.countSubscribers) || undefined;
            let clicksCount = undefined;

            if (metric.payload && metric.payload.counters) {
                clicksCount =
                    metric.type === 'promoCampaign'
                        ? metric.payload.counters.countTransitions || 0
                        : metric.payload.counters.clicksCounts || 0;
            }

            // Get cross-pollination earnings from the map instead of metric.payload
            const crossPollinationEarnings = crossPollinationEarningsMap.get(metric._id);

            return {
                username: metric.subscriber?.username || 'N/A',
                foreignId: metric.foreignId,
                type: metricTypeName(metric.type),
                active: metric.payload.active || false,
                createdAt: (metric.payload && moment(metric.payload.createdAt).tz(timezone).format('L hh:mm a')) || '',
                name: (metric.payload && metric.payload.name) || '',
                platform: (metric.settings && metric.settings.platform) || '',
                associatedUsername: metric.associatedUsername || '',
                subscribersCount,
                claimsCount,
                clicksCount,
                earningsTotal,
                crossPollinationEarnings:
                    crossPollinationEarnings && typeof crossPollinationEarnings === 'object'
                        ? d2f(amountType === 'gross' ? crossPollinationEarnings.total : crossPollinationEarnings.totalNet)
                        : 0,
            };
        });

        const earningsTotal =
            group.earningsTotal && typeof group.earningsTotal === 'object'
                ? d2f(group.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                : 0;

        // Insert a row for border with separators in each member
        metrics.push({
            username: '-----',
            foreignId: '-----',
            type: '-----',
            active: '-----',
            createdAt: '-----',
            name: '-----',
            platform: '-----',
            associatedUsername: '-----',
            subscribersCount: '-----',
            claimsCount: '-----',
            clicksCount: '-----',
            earningsTotal: '-----',
            crossPollinationEarnings: '-----',
        });

        // Insert a row for the group summary
        metrics.push({
            username: '',
            foreignId: undefined,
            type: '',
            active: '',
            createdAt: group.createdAt ? moment(group.createdAt).tz(timezone).format('L hh:mm a') : '',
            name: group.name,
            platform: '',
            associatedUsername: '',
            subscribersCount: group.subscribersTotal !== 0 ? group.subscribersTotal : undefined,
            claimsCount: group.claimsTotal !== 0 ? group.claimsTotal : undefined,
            clicksCount: group.clicksTotal !== 0 ? group.clicksTotal : undefined,
            earningsTotal,
            // Sum cross-pollination earnings from the map
            crossPollinationEarnings: group.metrics.reduce((sum, metric) => {
                const cpEarnings = crossPollinationEarningsMap.get(metric._id);
                return (
                    sum +
                    (cpEarnings && typeof cpEarnings === 'object'
                        ? d2f(amountType === 'gross' ? cpEarnings.total : cpEarnings.totalNet)
                        : 0)
                );
            }, 0),
        });

        return metrics;
    });

    // Grand totals across all groups, matching the totals row at the bottom of the groups table
    const totalsRow = {
        username: 'Total',
        foreignId: undefined,
        type: '',
        active: '',
        createdAt: '',
        name: '',
        platform: '',
        associatedUsername: '',
        subscribersCount: rows.reduce((sum, group) => sum + (group.subscribersTotal || 0), 0),
        claimsCount: rows.reduce((sum, group) => sum + (group.claimsTotal || 0), 0),
        clicksCount: rows.reduce((sum, group) => sum + (group.clicksTotal || 0), 0),
        earningsTotal: rows.reduce(
            (sum, group) =>
                sum +
                (group.earningsTotal && typeof group.earningsTotal === 'object'
                    ? d2f(group.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                    : 0),
            0,
        ),
        crossPollinationEarnings: rows.reduce(
            (sum, group) =>
                sum +
                group.metrics.reduce((groupSum, metric) => {
                    const cpEarnings = crossPollinationEarningsMap.get(metric._id);
                    return (
                        groupSum +
                        (cpEarnings && typeof cpEarnings === 'object'
                            ? d2f(amountType === 'gross' ? cpEarnings.total : cpEarnings.totalNet)
                            : 0)
                    );
                }, 0),
            0,
        ),
    };

    return { headers, data, totalsRow };
};

type Props = {
    subscriber: any;
    metricType: 'trial' | 'campaign';
    amountType: 'gross' | 'net';
};

const SextforceMetricsGroups = (props: Props) => {
    const { subscriber, metricType, amountType } = props;
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const axios = useAxios();
    const params = useParams();
    const settingsContext = useContext(SettingsContext);

    const { dashboardAccount, dashboardAccountLoading } = useDashboardAccount(true);
    const [timezone, setTimezone] = useState<string>(moment.tz.guess());

    // Set global MomentJS locale
    moment.locale('en-gb');

    useEffect(() => {
        if (!dashboardAccountLoading && dashboardAccount) {
            if (dashboardAccount && dashboardAccount.timezone) {
                setTimezone(dashboardAccount.timezone);
            }
        }
    }, [dashboardAccountLoading, dashboardAccount]);

    // Subscribers
    const { data: subscribers, isLoading: subscribersLoading } = useSubscribers();

    const [crossPollinationEarningsSubscriberId, setCrossPollinationEarningsSubscriberId] = useState<string>('none');

    // State to track cross-pollination earnings and loading state
    const [crossPollinationEarningsMap, setCrossPollinationEarningsMap] = useState<CrossPollinationEarningsMap>(new Map());
    const [crossPollinationLoadingMap, setCrossPollinationLoadingMap] = useState<CrossPollinationLoadingMap>(new Map());
    const [fetchProgress, setFetchProgress] = useState({ total: 0, fetched: 0 });
    const [fetchError, setFetchError] = useState<string | null>(null);

    const { data: groups, isLoading: isGroupsLoading, metricsGroupDelete } = useSextforceMetricsGroups(metricType);

    const [groupDialogOpen, setGroupDialogOpen] = useState<boolean>(false);
    const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
    const [metricsGroup, setMetricsGroup] = useState<OnlyFansMetricsGroupWithSummary | null>(null);

    // Track the abort controller of the fetch in flight
    const abortControllerRef = useRef<AbortController | null>(null);

    // Function to cancel fetching
    const handleCancelFetch = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Clear loading states
        const clearedLoadingMap = new Map<string, boolean>();
        crossPollinationLoadingMap.forEach((_, key) => {
            clearedLoadingMap.set(key, false);
        });
        setCrossPollinationLoadingMap(clearedLoadingMap);

        // Reset progress
        setFetchProgress(prev => ({ ...prev, fetched: prev.total }));
        setFetchError(null);
    };

    // Selecting a cross-pollination account no longer fetches anything by itself -- the
    // calculation is heavy on the database, so it only runs when the user presses the
    // Calculate button. Changing the selection (or the groups) just clears stale results.
    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        setCrossPollinationEarningsMap(new Map());
        setCrossPollinationLoadingMap(new Map());
        setFetchProgress({ total: 0, fetched: 0 });
        setFetchError(null);
    }, [groups, crossPollinationEarningsSubscriberId]);

    // Abort any in-flight fetch on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Fetch deduplicated cross-pollination earnings one group at a time to keep the load on
    // the database down. Within each group every fan is attributed to exactly one of the
    // group's metric links (their first touch within the group), so per-metric and per-group
    // totals never double-count a fan who claimed several of the group's links.
    const handleFetchCrossPollinationEarnings = async () => {
        setFetchError(null);

        if (!groups || crossPollinationEarningsSubscriberId === 'none' || crossPollinationEarningsSubscriberId === '') {
            return;
        }

        // The calculation runs live over every fan and transaction involved, so warn the
        // user before kicking it off
        try {
            await dialog.confirm({
                title: 'Calculate Cross-Pollination Earnings',
                message:
                    'This calculation analyses every fan and every transaction in your groups, so it can take several minutes to complete depending on the amount of data. The results will appear group by group as they are ready. Please be patient and keep this page open while it runs.',
                ok: {
                    color: 'primary',
                    text: 'Calculate',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            });
        } catch {
            return;
        }

        // Extract all metrics from all groups
        const allMetrics = groups.flatMap(group => group.metrics);

        if (allMetrics.length === 0) {
            return;
        }

        // The cross accounts to check spending on: 'all' is resolved to the whole Cognito
        // group server-side, mirroring the legacy route
        const crossSubscriberIds: string[] | 'all' =
            crossPollinationEarningsSubscriberId === 'all' ? 'all' : [crossPollinationEarningsSubscriberId];

        // Cancel any ongoing fetches
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller
        abortControllerRef.current = new AbortController();

        // Only groups that actually contain metrics need a request
        const groupsWithMetrics = groups.filter(group => group.metrics.length > 0);

        const newLoadingMap = new Map<string, boolean>();

        // Initialize loading map
        allMetrics.forEach(metric => {
            newLoadingMap.set(metric._id, true);
        });

        setCrossPollinationLoadingMap(new Map(newLoadingMap));
        setFetchProgress({ total: groupsWithMetrics.length, fetched: 0 });

        const newEarningsMap = new Map<string, MetricCrossSubscriberEarningsResult | null>();

        const fetchGroupsSequentially = async () => {
            const query = `${settingsContext.routes.metrics.base}${params.userId}/crossEarnings/deduped/pg`;

            let fetchedCount = 0;

            for (const group of groupsWithMetrics) {
                // Check if aborted
                if (abortControllerRef.current?.signal.aborted) {
                    console.log('Fetch aborted');
                    break;
                }

                try {
                    const response = await axios.post(
                        query,
                        {
                            metricIds: group.metrics.map(metric => metric._id),
                            crossSubscriberIds,
                        },
                        {
                            signal: abortControllerRef.current?.signal,
                        },
                    );
                    const data = response.data as DedupedCrossEarningsResponse;

                    // Start every metric of this group at zero so links whose fans were
                    // attributed elsewhere (or who spent nothing) show 0 rather than a blank
                    group.metrics.forEach(metric => {
                        newEarningsMap.set(metric._id, {
                            _id: metric._id,
                            total: f2d(0),
                            totalNet: f2d(0),
                            totalBefore: f2d(0),
                            totalBeforeNet: f2d(0),
                            totalWithoutSubscription: f2d(0),
                            totalNetWithoutSubscription: f2d(0),
                            totalBeforeWithoutSubscription: f2d(0),
                            totalBeforeNetWithoutSubscription: f2d(0),
                        });
                    });

                    // Sum each metric's rows across the selected cross accounts. Rows are
                    // disjoint within the group by construction, so this sum is duplicate-free.
                    data.rows.forEach(row => {
                        const earnings = newEarningsMap.get(row.metricId);

                        if (earnings) {
                            earnings.total = f2d(d2f(earnings.total) + d2f(row.total));
                            earnings.totalNet = f2d(d2f(earnings.totalNet) + d2f(row.totalNet));
                            earnings.totalBefore = f2d(d2f(earnings.totalBefore) + d2f(row.totalBefore));
                            earnings.totalBeforeNet = f2d(d2f(earnings.totalBeforeNet) + d2f(row.totalBeforeNet));
                            earnings.totalWithoutSubscription = f2d(
                                d2f(earnings.totalWithoutSubscription) + d2f(row.totalWithoutSubscription),
                            );
                            earnings.totalNetWithoutSubscription = f2d(
                                d2f(earnings.totalNetWithoutSubscription) + d2f(row.totalNetWithoutSubscription),
                            );
                            earnings.totalBeforeWithoutSubscription = f2d(
                                d2f(earnings.totalBeforeWithoutSubscription) + d2f(row.totalBeforeWithoutSubscription),
                            );
                            earnings.totalBeforeNetWithoutSubscription = f2d(
                                d2f(earnings.totalBeforeNetWithoutSubscription) + d2f(row.totalBeforeNetWithoutSubscription),
                            );
                        }
                    });
                } catch (error: any) {
                    if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                        console.error(`Error fetching deduped cross-pollination earnings for group ${group._id}:`, error);

                        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cross-pollination earnings';
                        setFetchError(errorMessage);

                        // Abort remaining groups; the Calculate button re-runs the selection
                        if (abortControllerRef.current) {
                            abortControllerRef.current.abort();
                        }
                    } else {
                        console.log('Request was cancelled');
                    }

                    break;
                }

                fetchedCount++;

                // Publish this group's results and progress before moving to the next group
                setCrossPollinationEarningsMap(new Map(newEarningsMap));
                setFetchProgress({ total: groupsWithMetrics.length, fetched: fetchedCount });

                group.metrics.forEach(metric => {
                    newLoadingMap.set(metric._id, false);
                });
                setCrossPollinationLoadingMap(new Map(newLoadingMap));
            }

            // Whatever happened, nothing is loading any more
            allMetrics.forEach(metric => {
                newLoadingMap.set(metric._id, false);
            });
            setCrossPollinationLoadingMap(new Map(newLoadingMap));
            setFetchProgress(prev => ({ ...prev, fetched: prev.total }));
        };

        fetchGroupsSequentially();
    };

    const openGroupDialog = (metricId: string | null) => {
        if (metricId && !groups) {
            return;
        }

        setGroupDialogOpen(true);

        if (groups && metricId) {
            setMetricsGroup(groups.find(group => group._id === metricId) || null);
        }
    };

    const handleGroupDialogClose = () => {
        setGroupDialogOpen(false);
        setMetricsGroup(null);
    };

    const openShareDialog = (metricId: string | null) => {
        if (metricId && !groups) {
            return;
        }

        setShareDialogOpen(true);

        if (groups && metricId) {
            setMetricsGroup(groups.find(group => group._id === metricId) || null);
        }
    };

    const handleShareDialogClose = () => {
        setShareDialogOpen(false);
        setMetricsGroup(null);
    };

    const handleDeleteGroup = (groupId: string) => {
        dialog
            .confirm({ title: 'Delete Group', message: 'Are you sure you want to delete this group?' })
            .then(() => {
                metricsGroupDelete(groupId);
            })
            .catch(() => {});
    };

    // Convert report to CSV format and start file download
    const handleDownloadReportCsv = () => {
        if (!groups) {
            return;
        }

        const transformedData = transformTransactionsForExport(groups, amountType, timezone, crossPollinationEarningsMap);

        const stackedData: {
            username: string;
            foreignId: number | string | undefined;
            type: string;
            active: string | boolean;
            createdAt: string;
            name: string;
            platform: string;
            associatedUsername: string;
            subscribersCount: number | string | undefined;
            claimsCount: number | string | undefined;
            earningsTotal: number | string;
            crossPollinationEarnings: number | string;
        }[] = [];

        transformedData.data.forEach(group => {
            group.forEach(metric => {
                stackedData.push(metric);
            });

            // Insert a spacer row
            stackedData.push({
                username: '',
                foreignId: '',
                type: '',
                active: '',
                createdAt: '',
                name: '',
                platform: '',
                associatedUsername: '',
                subscribersCount: '',
                claimsCount: '',
                earningsTotal: '',
                crossPollinationEarnings: '',
            });
        });

        // Grand totals row across all groups
        stackedData.push(transformedData.totalsRow);

        // Format JSON data to CSV
        const csv = parseCsv(stackedData, {
            fields: transformedData.headers,
            delimiter: ',',
            transforms: [],
        });

        // Convert CSV to Blob
        const blob: Blob = new Blob([csv], { type: 'text/csv' });

        // Create a descriptive filename
        const filename: string = `groups_${moment().tz(timezone, true).format('YYYY-MM-DD')}.csv`;

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode && link.parentNode.removeChild(link);
    };

    // Convert to Excel format and start file download
    const handleDownloadReportExcel = () => {
        if (!groups) {
            return;
        }

        const transformedData = transformTransactionsForExport(groups, amountType, timezone, crossPollinationEarningsMap);

        const workbook = XLSX.utils.book_new();

        transformedData.data.forEach((group, index) => {
            const worksheet = XLSX.utils.aoa_to_sheet([]);

            XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map(header => header.label)]);

            XLSX.utils.sheet_add_json(worksheet, group, {
                origin: 'A2',
                skipHeader: true,
                header: transformedData.headers.map((header: any) => header.value),
            });

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                `${index} - ${(group[group.length - 1].name as string).replace(/[^a-z0-9]/gi, '_').substring(0, 25)}`,
            );
        });

        // Grand totals sheet across all groups
        const totalsWorksheet = XLSX.utils.aoa_to_sheet([]);

        XLSX.utils.sheet_add_aoa(totalsWorksheet, [transformedData.headers.map(header => header.label)]);

        XLSX.utils.sheet_add_json(totalsWorksheet, [transformedData.totalsRow], {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map((header: any) => header.value),
        });

        XLSX.utils.book_append_sheet(workbook, totalsWorksheet, 'Totals');

        // Create a descriptive filename
        const filename: string = `groups_${moment().tz(timezone, true).format('YYYY-MM-DD')}.xlsx`;

        XLSX.writeFile(workbook, filename, { bookType: 'xlsx', bookSST: false, cellStyles: true });
    };

    const isFetchingCrossPollinationEarnings = fetchProgress.total > 0 && fetchProgress.fetched < fetchProgress.total;

    return (
        <Card sx={{ width: '100%', marginBottom: 1 }}>
            <CardContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'}>
                            <Typography variant="h6">Grouped Metrics</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title="Download as CSV">
                                    <span>
                                        <IconButton disabled={isGroupsLoading || !groups} onClick={async () => handleDownloadReportCsv()}>
                                            <FontAwesomeIcon
                                                icon={faFileCsv}
                                                size="2x"
                                                color={isGroupsLoading ? theme.palette.text.disabled : theme.palette.info.main}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Download as Excel">
                                    <span>
                                        <IconButton disabled={isGroupsLoading || !groups} onClick={() => handleDownloadReportExcel()}>
                                            <FontAwesomeIcon
                                                icon={faFileExcel}
                                                size="2x"
                                                color={isGroupsLoading ? theme.palette.text.disabled : theme.palette.info.main}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Button
                                    onClick={() => openGroupDialog('')}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<JoinFullIcon />}
                                >
                                    Create
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        Group metrics from different pages in your dashboard to see the combined earnings. You can group tracking links
                        (campaigns) and trials together from the same and multiple pages as necessary.
                    </Grid>

                    <Grid item xs={12}>
                        <Stack direction="column" spacing={1}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Select your other account and click Calculate to load the cross-pollination earnings.
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <div style={{ flexGrow: 1 }}>
                                    <SextforceMetricsCrossReferenceEarningsSubscriberSelector
                                        subscribers={subscribers}
                                        subscribersLoading={subscribersLoading}
                                        crossReferenceSubscriberId={crossPollinationEarningsSubscriberId}
                                        setCrossReferenceSubscriberId={setCrossPollinationEarningsSubscriberId}
                                    />
                                </div>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="medium"
                                    startIcon={<CalculateIcon />}
                                    disabled={
                                        isGroupsLoading ||
                                        !groups ||
                                        groups.length === 0 ||
                                        crossPollinationEarningsSubscriberId === 'none' ||
                                        crossPollinationEarningsSubscriberId === '' ||
                                        isFetchingCrossPollinationEarnings
                                    }
                                    onClick={handleFetchCrossPollinationEarnings}
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0, marginTop: '4px' }}
                                >
                                    Calculate
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Progress indicator for cross-pollination earnings fetching */}
                    {isFetchingCrossPollinationEarnings && (
                        <Grid item xs={12}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="textSecondary">
                                    Calculating cross-pollination earnings: group {(fetchProgress.fetched + 1).toLocaleString()} of{' '}
                                    {fetchProgress.total.toLocaleString()}
                                </Typography>
                                <Stack direction="row" alignItems="center">
                                    <LinearProgress
                                        variant="determinate"
                                        value={(fetchProgress.fetched / Math.max(fetchProgress.total, 1)) * 100}
                                        sx={{
                                            flexGrow: 1,
                                            marginRight: 1,
                                        }}
                                    />
                                    <IconButton size="small" color="error" onClick={handleCancelFetch}>
                                        <CancelIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Grid>
                    )}

                    {/* Error message if fetching failed */}
                    {fetchError && (
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                                <Typography variant="body2" color="error">
                                    Error: {fetchError}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Fetching was stopped due to an error. You can try selecting the cross-pollination account again to
                                    retry.
                                </Typography>
                            </Stack>
                        </Grid>
                    )}

                    {isGroupsLoading ? (
                        <Grid item xs={12}>
                            <Skeleton variant="rectangular" width="100%" height={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            {groups && groups.length > 0 ? (
                                <SextforceMetricsGroupsTable
                                    subscriber={subscriber}
                                    groups={groups}
                                    amountType={amountType}
                                    openGroupDialog={openGroupDialog}
                                    openShareDialog={openShareDialog}
                                    handleDeleteGroup={handleDeleteGroup}
                                    timezone={timezone}
                                    crossPollinationEarningsMap={crossPollinationEarningsMap}
                                    crossPollinationLoadingMap={crossPollinationLoadingMap}
                                    crossPollinationEarningsSubscriberId={crossPollinationEarningsSubscriberId}
                                />
                            ) : (
                                <Stack
                                    spacing={1}
                                    direction="row"
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ width: '100%', height: 50 }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        No groups found
                                    </Typography>
                                </Stack>
                            )}
                        </Grid>
                    )}
                </Grid>

                {groupDialogOpen && (
                    <SextforceMetricsGroupDialog
                        open={groupDialogOpen}
                        onClose={handleGroupDialogClose}
                        subscriber={subscriber}
                        metricType={metricType}
                        metricsGroup={metricsGroup}
                    />
                )}

                {shareDialogOpen && metricsGroup && (
                    <SextforceMetricsGroupsShareDialog
                        open={shareDialogOpen}
                        onClose={handleShareDialogClose}
                        subscriber={subscriber}
                        metricsGroup={metricsGroup}
                        theme={theme}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsGroups;
